<template>
  <b-button
    class="copy-btn"
    :type="type"
    :size="size"
    :icon-left="currentIcon"
    :title="success ? copiedTitle : title"
    :aria-label="ariaLabel || title"
    @click.stop="handleClick"
  />
</template>

<script lang="ts">
import { defineComponent, ref, watch, onBeforeUnmount } from 'vue'

export default defineComponent({
  name: 'CopyToClipboard',
  props: {
    value: { type: [String, Number] as unknown as () => string | number, required: true },
    title: { type: String, default: 'Copy' },
    copiedTitle: { type: String, default: 'Copied' },
    size: { type: String, default: 'is-small' },
    type: { type: String, default: 'is-text' },
    ariaLabel: { type: String, default: '' }
  },
  setup(props) {
    const success = ref(false)
    let resetTimer: number | undefined

    const currentIcon = ref('content-copy')

    watch(success, (val) => {
      currentIcon.value = val ? 'check' : 'content-copy'
    }, { immediate: true })

    const reset = () => {
      success.value = false
      if (resetTimer) {
        window.clearTimeout(resetTimer)
        resetTimer = undefined
      }
    }

    const fallbackCopyTextToClipboard = (text: string) => {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()
      try { document.execCommand('copy') } catch (_e) { /* ignore */ }
      document.body.removeChild(textarea)
    }

    const copy = async (text: string) => {
      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          fallbackCopyTextToClipboard(text)
        }
      } catch (_e) {
        fallbackCopyTextToClipboard(text)
      }
    }

    const handleClick = async () => {
      const text = String(props.value ?? '')
      if (!text) return
      await copy(text)
      success.value = true
      if (resetTimer) window.clearTimeout(resetTimer)
      resetTimer = window.setTimeout(() => { success.value = false; resetTimer = undefined }, 1000)
    }

    onBeforeUnmount(() => { if (resetTimer) window.clearTimeout(resetTimer) })

    return { success, currentIcon, handleClick }
  }
})
</script>

<style scoped>
  .copy-btn {
    transition: opacity 0.2s ease;
    opacity: 0.5;
  }
  .copy-btn:hover {
    opacity: 1;
  }
</style>


